<template>
  <manual-page
    title="Gestión de Mascotas"
    description="Guía completa para consultar perfiles de mascotas, ver sus dietas y dar seguimiento a su nutrición."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'petsIndex' }"
    action-text="Ir a Mascotas"
  >
    <!-- Overview -->
    <h2>Vista General</h2>
    <p>
      La sección de mascotas te permite consultar información detallada de las mascotas
      de tu sucursal. Cada mascota tiene un perfil completo con datos de salud, dieta actual
      y plan de alimentación.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Información disponible de cada mascota:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Datos básicos (nombre, especie, raza, edad)</li>
        <li>Información física (peso actual, condición corporal)</li>
        <li>Condiciones de salud y alergias</li>
        <li>Dieta actual con composición detallada</li>
        <li>Plan de alimentación activo</li>
        <li>Historial de evaluaciones</li>
        <li>Fotos de la mascota</li>
      </ul>
    </div>

    <!-- Pet List -->
    <h2>Lista de Mascotas</h2>
    <p>
      La lista muestra todas las mascotas de clientes asignados a tu sucursal
      con información resumida.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información en la Lista</h3>
          <p class="text-gray-600 mb-2">Cada mascota muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Foto:</strong> Imagen de perfil de la mascota</li>
            <li><strong>Nombre:</strong> Nombre de la mascota</li>
            <li><strong>Especie:</strong> Perro o gato</li>
            <li><strong>Raza:</strong> Raza registrada</li>
            <li><strong>Dueño:</strong> Nombre del cliente</li>
            <li><strong>Estado:</strong> Si tiene dieta activa</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Filtrar Mascotas</h3>
          <p class="text-gray-600">
            Puedes filtrar la lista por especie (perro/gato),
            por estado de dieta (activa/inactiva) o usar el buscador
            para encontrar una mascota específica.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Haz clic en cualquier mascota para ver su perfil completo
        con toda la información de salud y dieta.
      </p>
    </div>

    <!-- Pet Profile -->
    <h2>Perfil de la Mascota</h2>
    <p>
      El perfil contiene toda la información relevante para preparar
      correctamente la alimentación de cada mascota.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información Básica</h3>
          <p class="text-gray-600 mb-2">Datos generales de la mascota:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Identificación de la mascota</li>
            <li><strong>Especie:</strong> Perro o gato</li>
            <li><strong>Raza:</strong> Raza específica</li>
            <li><strong>Fecha de nacimiento:</strong> Edad exacta</li>
            <li><strong>Género:</strong> Macho o hembra</li>
            <li><strong>Esterilizado:</strong> Si está o no esterilizado</li>
            <li><strong>Nivel de actividad:</strong> Bajo, medio o alto</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Datos de Salud</h3>
          <p class="text-gray-600 mb-2">Información física y de salud:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Peso actual:</strong> Último peso registrado</li>
            <li><strong>Peso ideal:</strong> Meta de peso según la evaluación</li>
            <li><strong>Condición corporal:</strong> Escala 1-9</li>
            <li><strong>Alergias:</strong> Ingredientes que debe evitar</li>
            <li><strong>Condiciones médicas:</strong> Problemas de salud conocidos</li>
            <li><strong>Medicamentos:</strong> Tratamientos actuales</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Siempre revisa las alergias y condiciones médicas
        antes de preparar el alimento. Asegúrate de seguir las indicaciones especiales
        para cada mascota.
      </p>
    </div>

    <!-- Diet Information -->
    <h2>Ver Dieta de la Mascota</h2>
    <p>
      La dieta asignada a cada mascota define exactamente qué ingredientes
      y en qué cantidades debe contener su alimento.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Tipo de Dieta</h3>
          <p class="text-gray-600">
            La dieta indica el tipo general de alimentación (BARF, cocido, mixto)
            y la fórmula específica asignada según las necesidades de la mascota.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Composición Detallada</h3>
          <p class="text-gray-600 mb-2">La dieta muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Proteínas:</strong> Tipos de carne y cantidades</li>
            <li><strong>Vegetales:</strong> Verduras incluidas</li>
            <li><strong>Carbohidratos:</strong> Si aplica al tipo de dieta</li>
            <li><strong>Suplementos:</strong> Vitaminas y minerales adicionales</li>
            <li><strong>Cantidad total:</strong> Gramos por comida</li>
            <li><strong>Comidas al día:</strong> Frecuencia recomendada</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cálculo de Porciones</h3>
          <p class="text-gray-600">
            Las cantidades están calculadas según el peso, edad y nivel de actividad
            de la mascota. Sigue las indicaciones exactas para cada porción.
          </p>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Nota:</strong> La dieta también aparece en el detalle de cada orden,
        así que puedes consultarla directamente al preparar el alimento.
      </p>
    </div>

    <!-- Plan Information -->
    <h2>Plan de Alimentación</h2>
    <p>
      El plan de alimentación define la frecuencia de entregas y el estado
      de la suscripción de la mascota.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información del Plan</h3>
          <p class="text-gray-600 mb-2">El plan muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Estado:</strong> Activo, pausado o cancelado</li>
            <li><strong>Frecuencia:</strong> Cada cuántas semanas se entrega</li>
            <li><strong>Tipo de entrega:</strong> Envío o recolección</li>
            <li><strong>Próxima entrega:</strong> Fecha programada</li>
            <li><strong>Cantidad por ciclo:</strong> Total de alimento por entrega</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Estados del Plan</h3>
          <p class="text-gray-600 mb-2">Los planes pueden estar en diferentes estados:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Activo:</strong> Generando órdenes normalmente</li>
            <li><strong>Pausado:</strong> Temporalmente suspendido por el cliente</li>
            <li><strong>Suspendido por pago:</strong> Problemas con el cobro</li>
            <li><strong>Cancelado:</strong> Plan terminado definitivamente</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Health Tracking -->
    <h2>Seguimiento de Salud</h2>
    <p>
      El perfil de la mascota incluye un historial de evaluaciones que permite
      dar seguimiento a su progreso.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Historial de Peso</h3>
          <p class="text-gray-600">
            Puedes ver cómo ha evolucionado el peso de la mascota con el tiempo.
            Esto ayuda a identificar si está ganando o perdiendo peso
            según lo esperado.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Evaluaciones Registradas</h3>
          <p class="text-gray-600 mb-2">El historial de evaluaciones incluye:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Fecha de cada evaluación</li>
            <li>Peso registrado</li>
            <li>Condición corporal evaluada</li>
            <li>Observaciones y notas</li>
            <li>Quién realizó la evaluación</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Registrar Evaluación</h3>
          <p class="text-gray-600">
            Si tienes permiso, puedes registrar nuevas evaluaciones cuando
            el cliente trae a su mascota para pesaje o chequeo nutricional.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Se recomienda hacer evaluaciones cada 4-6 semanas para
        mascotas en proceso de ajuste de peso, y cada 2-3 meses para mantenimiento.
      </p>
    </div>

    <!-- Search and Filter -->
    <h2>Buscar Mascotas</h2>
    <p>
      Usa las herramientas de búsqueda para encontrar mascotas específicas.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Búsqueda por Texto</h3>
          <p class="text-gray-600">
            Escribe en el buscador el nombre de la mascota o del dueño.
            La búsqueda funciona con coincidencias parciales.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Filtros Disponibles</h3>
          <p class="text-gray-600 mb-2">Puedes filtrar por:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Especie:</strong> Solo perros o solo gatos</li>
            <li><strong>Estado de dieta:</strong> Con dieta activa o sin dieta</li>
            <li><strong>Raza:</strong> Filtrar por raza específica</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Orders for Pet -->
    <h2>Órdenes de la Mascota</h2>
    <p>
      Desde el perfil de la mascota puedes ver todas sus órdenes de alimento.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Historial de Órdenes</h3>
          <p class="text-gray-600">
            Lista de todas las órdenes de esa mascota, tanto pasadas como actuales.
            Puedes ver las fechas de entrega, estados y composición de cada orden.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Próxima Orden</h3>
          <p class="text-gray-600">
            Si la mascota tiene un plan activo, verás la información de su
            próxima orden programada: fecha de entrega, estado de pago y dieta.
          </p>
        </div>
      </div>
    </div>

    <div class="info-box">
      <p class="text-sm text-gray-700">
        <strong>Utilidad:</strong> Consultar el historial de órdenes te ayuda a
        verificar entregas pasadas o resolver dudas del cliente sobre pedidos anteriores.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Prácticas</h2>
    <ul>
      <li><strong>Revisa el perfil completo:</strong> Antes de preparar, consulta alergias y condiciones</li>
      <li><strong>Sigue la dieta exacta:</strong> Respeta las cantidades e ingredientes indicados</li>
      <li><strong>Verifica las notas:</strong> Lee las instrucciones especiales de cada mascota</li>
      <li><strong>Registra observaciones:</strong> Si notas algo importante, documéntalo</li>
      <li><strong>Mantén el historial actualizado:</strong> Registra evaluaciones cuando sea posible</li>
      <li><strong>Comunica cambios:</strong> Si detectas algo inusual, informa al equipo central</li>
    </ul>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes dudas sobre la información de mascotas:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualPets",
  components: { ManualPage },
  data() {
    return {
      relatedLinks: [
        { route: "manualClients", label: "Clientes" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualProductionSchedule", label: "Programa de Producción" },
        { route: "manualGettingStarted", label: "Primeros Pasos" },
      ],
    };
  },
};
</script>
